/**
 * "Reports not recorded" on My Day — see unrecorded.ts for the rule. Each row
 * leads to where the report is written: the delivery form of a started
 * session, the booking (where Start lives) of one never started, or the
 * group occurrence for its shared report. Absent when there is nothing to do
 * and for anyone who does not deliver sessions.
 */

import { Link } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import a from '../access/access.module.css';
import { Button, Chip } from '@/ui/kit';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { useSession } from '@/core/auth/SessionProvider';
import { PERM } from '@/core/authz/permissions';
import { useLocale } from '@/core/i18n/LocaleProvider';
import { formatTime, formatWeekdayDate } from '@/core/calendar/format';
import { useClinicSettings } from '../patients/useClinicSettings';
import { dayBounds, localDateISO, shiftDateISO } from '../scheduling/calendarMath';
import { useBookingsWindow, useGroupSessionsWindow } from '../scheduling/useSchedulingData';
import { bookingPatientName } from '../scheduling/bookingRules';
import { UNRECORDED_DAYS, unrecordedItems } from './unrecorded';

export function UnrecordedSessions() {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) =>
    intl.formatMessage({ id }, v) as string;
  const authz = useAuthz();
  const { session } = useSession();
  const { locale } = useLocale();
  const settings = useClinicSettings();
  const myUid = session.status === 'signedIn' ? session.me.uid : null;
  const tz = settings?.timezone ?? 'Asia/Tehran';
  const cal = settings?.calendarSystem ?? 'jalali';
  const today = localDateISO(new Date(), tz);
  const before = dayBounds(today, tz).from;
  const from = dayBounds(shiftDateISO(today, -UNRECORDED_DAYS), tz).from;

  const on = authz.can(PERM.bookingsRead) && authz.can(PERM.sessionsDeliver) && myUid !== null;
  const mine = useBookingsWindow(
    on ? { from, to: before, practitionerUid: myUid, status: ['booked'] } : null,
  );
  const groups = useGroupSessionsWindow(on ? { from, to: before } : null);
  if (!on) return null;

  const items = unrecordedItems(mine.data ?? [], groups.data?.items ?? [], {
    myUid,
    before,
  });
  const failed = mine.isLoadingError || groups.isLoadingError;
  if (items.length === 0 && !failed) return null;

  const when = (iso: string) =>
    `${formatWeekdayDate(iso, { locale, calendar: cal, timezone: tz })} · ${formatTime(iso, { locale, timezone: tz })}`;

  return (
    <section
      className={a.sectionCard}
      style={{ inlineSize: '100%', maxInlineSize: 860 }}
      aria-labelledby="md-unrecorded"
    >
      <div style={{ padding: '14px 18px 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <strong id="md-unrecorded" style={{ color: 'var(--zc-teal-deep)' }}>
            {t('md.unrec.title')}
          </strong>
          {items.length > 0 && <Chip kind="warn">{intl.formatNumber(items.length)}</Chip>}
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--zc-muted)', marginBlockStart: 2 }}>
          {t('md.unrec.sub', { days: UNRECORDED_DAYS })}
        </p>
      </div>
      {failed && (
        <div className={a.row}>
          <span className={a.rowValue} style={{ fontSize: 12.5, color: 'var(--zc-err-ink)' }}>
            {t('rec.sectionFailed')}
          </span>
          <Button
            variant="quiet"
            onClick={() => {
              void mine.refetch();
              void groups.refetch();
            }}
          >
            {t('rec.retry')}
          </Button>
        </div>
      )}
      {items.map((it) => (
        <div key={`${it.kind}:${it.id}`} className={a.row} style={{ flexWrap: 'wrap' }}>
          <span className={a.rowKey} style={{ minInlineSize: 200, flex: '0 1 240px' }}>
            {when(it.startsAt)}
          </span>
          <span className={a.rowValue} style={{ flex: '1 1 200px' }}>
            {it.kind === 'booking' ? (
              <>
                <span dir="auto" style={{ color: 'var(--zc-ink-strong)' }}>
                  {bookingPatientName(it.booking)}
                </span>
                <span
                  dir="auto"
                  style={{ display: 'block', fontSize: 12.5, color: 'var(--zc-muted)' }}
                >
                  {it.booking.service.name}
                </span>
              </>
            ) : (
              <>
                <span dir="auto" style={{ color: 'var(--zc-ink-strong)' }}>
                  {it.group.service.name}
                </span>
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--zc-muted)' }}>
                  {t('ss.groupChip')}
                </span>
              </>
            )}
          </span>
          {it.kind === 'group' ? (
            <Chip kind="warn">{t('md.unrec.groupReport')}</Chip>
          ) : it.booking.executionStatus === 'in_progress' ? (
            <Chip kind="info">{t('bk.exec.in_progress')}</Chip>
          ) : (
            <Chip kind="warn">{t('ss.chip.not_recorded')}</Chip>
          )}
          <span className={a.rowAction}>
            {it.kind === 'group' ? (
              <Link to="/calendar/groups/$groupId" params={{ groupId: it.id }}>
                <Button variant="quiet">{t('md.unrec.writeGroup')}</Button>
              </Link>
            ) : it.booking.executionStatus === 'in_progress' ? (
              // A started session goes straight to its delivery form.
              <Link to="/calendar/bookings/$bookingId/deliver" params={{ bookingId: it.id }}>
                <Button variant="brand">{t('md.unrec.record')}</Button>
              </Link>
            ) : (
              // ZOY-551: never started — the booking is where Start lives.
              <Link to="/calendar/bookings/$bookingId" params={{ bookingId: it.id }}>
                <Button variant="brand">{t('md.unrec.record')}</Button>
              </Link>
            )}
          </span>
        </div>
      ))}
    </section>
  );
}
